import React, { useState } from 'react';
import { Button, Space, Tag, Modal, message, Image, Tooltip, Typography } from 'antd';
import { DownloadOutlined, DeleteOutlined, EyeOutlined, FileTextOutlined, CameraOutlined } from '@ant-design/icons';
import { useDeleteDocumentMutation } from '../../store/api/documentsApi';
import { Document } from '../../types';
import { formatDocumentUiDateTime } from '../../utils/documentUiFormat';
import { DataTable } from '../../ui';
import { documentTypeLabel } from '../../utils/enumUiLabels';
import { resolveDocumentDisplayTypeLabel } from '../../utils/documentDisplayName';
import {
  downloadDocument,
  fetchDocumentObjectUrl,
  resolveDocumentFilename,
  documentPreviewEndpoint,
  viewDocument,
} from '../../utils/documentDownload';

const { Title } = Typography;

interface DocumentsListProps {
  documents: Document[];
  loading?: boolean;
  onRefresh?: () => void;
  /** Разделить отображение на клиентские / перевозчик / прочие */
  groupByClientCarrier?: boolean;
}

const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  loading = false,
  onRefresh,
  groupByClientCarrier = false,
}) => {
  const [deleteDocument] = useDeleteDocumentMutation();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewRevoke, setPreviewRevoke] = useState<(() => void) | null>(null);
  const [previewFileType, setPreviewFileType] = useState<string>('');

  const closePreview = () => {
    previewRevoke?.();
    setPreviewVisible(false);
    setPreviewImage('');
    setPreviewRevoke(null);
    setPreviewFileType('');
  };

  const handleDownload = async (doc: Document) => {
    try {
      await downloadDocument(doc.id, doc);
    } catch (error) {
      console.error('Ошибка скачивания:', error);
      message.error('Ошибка при скачивании файла');
    }
  };

  const handleDelete = (doc: Document) => {
    Modal.confirm({
      title: 'Удаление документа',
      content: 'Вы уверены, что хотите удалить этот документ?',
      okText: 'Удалить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteDocument(doc.id).unwrap();
          message.success('Документ удален');
          onRefresh?.();
        } catch (error) {
          console.error('Ошибка удаления:', error);
          message.error('Ошибка при удалении документа');
        }
      },
    });
  };

  const handlePreview = async (doc: Document) => {
    try {
      if ((doc.fileType || 'file') !== 'photo') {
        await viewDocument(doc.id);
        return;
      }

      const { url, revoke } = await fetchDocumentObjectUrl(doc.id, documentPreviewEndpoint(doc.fileType));
      setPreviewImage(url);
      setPreviewRevoke(() => revoke);
      setPreviewFileType(doc.fileType || 'file');
      setPreviewVisible(true);
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      message.error('Ошибка при загрузке файла');
    }
  };

  const getStageColor = (stage?: string) => {
    switch (stage) {
      case 'loading': return 'blue';
      case 'unloading': return 'green';
      case 'other': return 'orange';
      default: return 'default';
    }
  };

  const getStageText = (stage?: string) => {
    switch (stage) {
      case 'loading': return 'Погрузка';
      case 'unloading': return 'Выгрузка';
      case 'other': return 'Другое';
      default: return 'Не указан';
    }
  };

  const columns = [
    {
      title: 'Тип',
      key: 'fileType',
      width: 80,
      render: (doc: Document) => (
        <div style={{ textAlign: 'center' }}>
          {doc.fileType === 'photo' ? (
            <CameraOutlined style={{ fontSize: 20, color: '#1890ff' }} />
          ) : (
            <FileTextOutlined style={{ fontSize: 20, color: '#52c41a' }} />
          )}
        </div>
      ),
    },
    {
      title: 'Метка',
      key: 'flags',
      width: 160,
      render: (doc: Document) => (
        <Space size={4} wrap>
          {doc.isOriginalTN ? <Tag color="magenta">Ориг. ТН</Tag> : null}
          {doc.isPrimaryDocs ? <Tag color="geekblue">Первичные</Tag> : null}
        </Space>
      )
    },
    {
      title: 'Название файла',
      key: 'fileName',
      render: (doc: Document) => {
        const fileName = resolveDocumentFilename(doc);
        return (
          <Tooltip title={fileName}>
            <span style={{ maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fileName}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Этап',
      key: 'stage',
      width: 120,
      render: (doc: Document) => (
        <Tag color={getStageColor(doc.stage)}>
          {getStageText(doc.stage)}
        </Tag>
      ),
    },
    {
      title: 'Тип документа',
      key: 'documentType',
      width: 180,
      render: (doc: Document) => (
        <Tag color="purple">
          {resolveDocumentDisplayTypeLabel(doc, documentTypeLabel)}
        </Tag>
      ),
    },
    {
      title: 'Комментарий',
      key: 'comment',
      render: (doc: Document) => (
        <Tooltip title={doc.comment || 'Нет комментария'}>
          <span style={{ maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.comment || 'Нет комментария'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Дата загрузки',
      key: 'createdAt',
      width: 150,
      render: (doc: Document) => (
        <span>{formatDocumentUiDateTime(doc.createdAt)}</span>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (doc: Document) => (
        <Space size="small">
          <Tooltip title="Просмотр">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => void handlePreview(doc)}
            />
          </Tooltip>
          <Tooltip title="Скачать">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => void handleDownload(doc)}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(doc)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const norm = (t?: string | null) => String(t || '').toUpperCase();
  const clientDocs = groupByClientCarrier ? documents.filter((d) => norm(d.documentType) === 'INVOICE') : [];
  const carrierDocs = groupByClientCarrier ? documents.filter((d) => ['CARRIER_INVOICE', 'LOADING_INVOICE'].includes(norm(d.documentType))) : [];
  const otherDocs = groupByClientCarrier ? documents.filter((d) => !['INVOICE', 'CARRIER_INVOICE', 'LOADING_INVOICE'].includes(norm(d.documentType))) : [];
  const useGrouped = groupByClientCarrier && (clientDocs.length > 0 || carrierDocs.length > 0 || otherDocs.length > 0);

  const renderTable = (data: Document[], title?: string) => (
    <div key={title || 'main'} style={title ? { marginBottom: 24 } : undefined}>
      {title && <Title level={5} style={{ marginBottom: 8 }}>{title}</Title>}
      <DataTable
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </div>
  );

  return (
    <>
      {useGrouped ? (
        <>
          {clientDocs.length > 0 && renderTable(clientDocs, 'Клиентские документы')}
          {carrierDocs.length > 0 && renderTable(carrierDocs, 'Документы перевозчика')}
          {otherDocs.length > 0 && renderTable(otherDocs, 'Прочие документы')}
        </>
      ) : (
        renderTable(documents)
      )}

      <Modal
        open={previewVisible}
        title="Просмотр файла"
        footer={null}
        onCancel={closePreview}
        width={800}
      >
        {previewFileType === 'photo' ? (
          <Image
            alt="preview"
            style={{ width: '100%' }}
            src={previewImage}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
        ) : null}
      </Modal>
    </>
  );
};

export default DocumentsList;
